import React, { useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardContainer from "../../components/dashboard-container";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import { parameterValueService } from "../../utils/services/parameter-value.service";
import ParameterValueModal from "../../components/parameterValueModal";
import ParameterValueList from "../../components/parameterValueList";
import { forecastParameterService } from "../../utils/services/forecast-parameter.service";
import { parameterFilterInitialState } from "../../utils/filter-config";
import buildQuery from "../../utils/query-builder";
import ImportIcon from "../../globalStyles/icons/importIcon";

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x
        ),
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };

    default:
      return state;
  }
}

const ParameterValue = () => {
  const { id: parameter_id } = useParams();
  const [parameter, setParameter] = useState({ name: "Parameter" });
  const [selectedData, setSelectedData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [parameterList, setParameterList] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [dataCount, setDataCount] = useState(rowsPerPage);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const text_color = theme?.palette?.text?.primary || "#FFF";
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const [filterData, dispatch] = useReducer(
    filterReducer,
    parameterFilterInitialState
  );
  const client_id = localStorage.getItem("client_id") || null;
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const navigate = useNavigate();

  const fetchParameterValues = async () => {
    setLoading(true);
    try {
      const response = await parameterValueService.fetch(
        parameter_id,
        buildQuery({ ...filterData, limit: rowsPerPage }, page),
        client_id
      );
      setParameterList(response?.data || []);
      setDataCount(response?.total || 0);
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParameterDetails = async () => {
    setLoading(true);
    try {
      const response = await forecastParameterService.get(parameter_id);
      setParameter(response);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (parameter_id) {
      fetchParameterDetails();
    }
  }, [parameter_id]);

  useEffect(() => {
    if (parameter_id) {
      fetchParameterValues();
    }
  }, [parameter_id, page, rowsPerPage, filterData]);

  if (!parameter_id) {
    return (
      <DashboardContainer header="Parameter Values">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            Parameter type is required in the URL.
          </Typography>
        </Box>
      </DashboardContainer>
    );
  }

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  return (
    <DashboardContainer
      header={`${parameter?.name}`}
      hideUploadButton
      title={"+ Add Value"}
      onClickAddButton={() => setOpenModal(true)}
      setViewMode={setViewMode}
      hideFilterClient
      filterButton={false}
      onClickRefresh={() => fetchParameterValues()}
      headerButtons={
        roles.find((x) =>
          ["Client - Standard", "Client - Premium"].includes(x.name)
        ) ? (
          <>
            <Button
              size="small"
              style={{
                textTransform: "none",
                marginRight: 10,
              }}
              variant="contained"
              color="primary"
              onClick={() => {
                localStorage.setItem("parameter_value_type", parameter_id);
                navigate("/parameter-value-import");
              }}
            >
              <ImportIcon fill={"#ffff"} />
              <Typography
                fontSize="14px"
                fontWeight={500}
                sx={{
                  color: text_color,
                  marginLeft: 1,
                }}
                fontFamily={"poppins"}
              >
                Import
              </Typography>
            </Button>
          </>
        ) : (
          <></>
        )
      }
    >
      <ParameterValueModal
        selectedParameter={selectedData}
        handleClose={() => {
          setOpenModal(false);
          setSelectedData(null);
        }}
        open={openModal}
        onAddData={fetchParameterValues}
        parameterType={parameter?.name}
        parameter_id={parameter_id}
        primary_color={primary_color}
      />
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <ParameterValueList
          dataCount={dataCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          setPage={setPage}
          onDeleteData={fetchParameterValues}
          onEditData={(e) => {
            setSelectedData(e);
            setOpenModal(true);
          }}
          data={parameterList}
          viewMode={viewMode}
          selectedParameter={selectedData}
          setSelectedParameter={setSelectedData}
          primary_color={primary_color}
          filterData={filterData}
          onChangeFilter={handleFilterChange}
          client_id={client_id}
        />
      )}
    </DashboardContainer>
  );
};

export default ParameterValue;
